if (!customElements.get('swk-timeline')) {
  customElements.define(
    'swk-timeline',
    class SwkTimeline extends HTMLElement {
      constructor() {
        super();
        this.onScrollTriggerRefresh = this.onScrollTriggerRefresh.bind(this);
        this.positionTrack = this.positionTrack.bind(this);
        this.activeIndex = -1;
      }

      connectedCallback() {
        this.track = this.querySelector('.swk-timeline__track');
        this.steps = [...this.querySelectorAll('.swk-timeline__step')];
        this.markers = [...this.querySelectorAll('.swk-timeline__marker')];
        this.lineProgress = this.querySelector('.swk-timeline__line-progress');

        if (!this.steps.length) return;

        this.positionTrack();
        this.initAnimation();

        window.addEventListener('load', this.onScrollTriggerRefresh, { once: true });
        window.addEventListener('resize', this.onScrollTriggerRefresh);
      }

      disconnectedCallback() {
        window.removeEventListener('load', this.onScrollTriggerRefresh);
        window.removeEventListener('resize', this.onScrollTriggerRefresh);
        this.ctx?.revert();
        this.ctx = null;
      }

      onScrollTriggerRefresh() {
        this.positionTrack();

        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      }

      positionTrack() {
        if (!this.track || this.steps.length < 1) return;

        const wrapperRect = this.getBoundingClientRect();
        const firstStep = this.steps[0];
        const lastStep = this.steps[this.steps.length - 1];
        const firstStepRect = firstStep.getBoundingClientRect();
        const lastStepRect = lastStep.getBoundingClientRect();
        const isMobile = window.matchMedia('(max-width: 650px)').matches;
        const extension = isMobile ? 32 : 48;

        // Run the line through each step (icon + text), not just marker-to-marker.
        const top = firstStepRect.top - wrapperRect.top - extension;
        const bottom = wrapperRect.height - (lastStepRect.bottom - wrapperRect.top) - extension;

        this.track.style.top = `${Math.max(0, top)}px`;
        this.track.style.bottom = `${Math.max(0, bottom)}px`;
        this.track.style.height = 'auto';
      }

      setActiveStep(index) {
        if (this.activeIndex === index) return;
        this.activeIndex = index;

        this.steps.forEach((step, i) => {
          const isActive = i === index;
          step.classList.toggle('is-active', isActive);

          if (this.prefersReducedMotion) return;

          gsap.to(step, {
            opacity: isActive ? 1 : this.isMobile ? 0.45 : 0.28,
            scale: isActive ? 1 : 0.94,
            filter: isActive ? 'blur(0px)' : this.isMobile ? 'blur(0px)' : 'blur(3px)',
            duration: 0.7,
            ease: 'power3.out',
            overwrite: 'auto',
          });

          const circle = step.querySelector('.swk-timeline__circle');
          if (circle) {
            step.classList.toggle('is-circle-glow', isActive);
          }

          if (isActive) {
            const ring = step.querySelector('.swk-timeline__marker-ring');
            if (ring) {
              gsap.fromTo(
                ring,
                { scale: 0.8, opacity: 0.7 },
                { scale: 2.6, opacity: 0, duration: 1.1, ease: 'power2.out', overwrite: true }
              );
            }

            const marker = step.querySelector('.swk-timeline__marker');
            if (marker) {
              gsap.fromTo(
                marker,
                { scale: 1 },
                { scale: 1.12, duration: 0.35, ease: 'power2.out', yoyo: true, repeat: 1 }
              );
            }
          }
        });
      }

      initAnimation() {
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
          window.addEventListener('load', () => this.initAnimation(), { once: true });
          return;
        }

        this.ctx?.revert();

        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.isMobile = window.matchMedia('(max-width: 650px)').matches;

        this.ctx = gsap.context(() => {
          gsap.registerPlugin(ScrollTrigger);

          this.positionTrack();

          if (!this.prefersReducedMotion) {
            this.initRevealAnimations();
            this.initParallax();
          } else {
            this.steps.forEach((step) => {
              step.classList.add('is-active');
              gsap.set(step, { opacity: 1, scale: 1, filter: 'none' });
            });
          }

          this.initLineAnimation();
          this.initSpotlight();
        }, this);
      }

      initRevealAnimations() {
        this.steps.forEach((step) => {
          const isReverse = step.classList.contains('swk-timeline__step--reverse');
          const content = step.querySelector('.swk-timeline__content');
          const contentItems = content ? [...content.children] : [];
          const marker = step.querySelector('.swk-timeline__marker');
          const circle = step.querySelector('.swk-timeline__circle');
          const contentX = this.isMobile ? 0 : isReverse ? 56 : -56;
          const visualX = this.isMobile ? 0 : isReverse ? -56 : 56;

          gsap.set(step, {
            opacity: 0.28,
            scale: 0.94,
            filter: this.isMobile ? 'none' : 'blur(3px)',
          });
          gsap.set(marker, { scale: 0, opacity: 0 });
          gsap.set(contentItems, { opacity: 0, y: 48, x: contentX });
          gsap.set(circle, {
            opacity: 0,
            scale: 0.55,
            rotation: isReverse ? -14 : 14,
            x: visualX,
          });

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: step,
              start: this.isMobile ? 'top 88%' : 'top 82%',
              once: true,
            },
          });

          tl.to(marker, {
            scale: 1,
            opacity: 1,
            duration: 0.65,
            ease: 'back.out(2.4)',
          })
            .to(
              circle,
              {
                opacity: 1,
                scale: 1,
                rotation: 0,
                x: 0,
                duration: 1,
                ease: 'power4.out',
              },
              '-=0.35'
            )
            .to(
              contentItems,
              {
                opacity: 1,
                y: 0,
                x: 0,
                duration: 0.75,
                stagger: 0.1,
                ease: 'power3.out',
              },
              '-=0.75'
            );
        });
      }

      initParallax() {
        if (this.isMobile) return;

        this.steps.forEach((step) => {
          const circle = step.querySelector('.swk-timeline__circle');
          if (!circle) return;

          gsap.to(circle, {
            y: -40,
            ease: 'none',
            scrollTrigger: {
              trigger: step,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 1.2,
            },
          });
        });
      }

      initLineAnimation() {
        if (!this.lineProgress || this.markers.length === 0) return;

        const firstMarker = this.markers[0];
        const lastMarker = this.markers[this.markers.length - 1];
        const dot = this.lineProgress.querySelector('.swk-timeline__line-dot');

        gsap.set(this.lineProgress, { scaleY: 0 });

        ScrollTrigger.create({
          trigger: firstMarker,
          start: 'center center',
          endTrigger: lastMarker,
          end: 'center center',
          scrub: this.prefersReducedMotion ? false : 0.85,
          onUpdate: (self) => {
            gsap.set(this.lineProgress, { scaleY: self.progress });

            if (dot && !this.prefersReducedMotion) {
              gsap.set(dot, {
                top: `${self.progress * 100}%`,
                opacity: self.progress > 0.02 && self.progress < 0.98 ? 1 : 0,
              });
            }
          },
        });
      }

      initSpotlight() {
        this.steps.forEach((step, index) => {
          ScrollTrigger.create({
            trigger: step,
            start: 'top 58%',
            end: 'bottom 42%',
            onEnter: () => this.setActiveStep(index),
            onEnterBack: () => this.setActiveStep(index),
          });
        });

        const initialIndex = this.steps.findIndex((step) => {
          const rect = step.getBoundingClientRect();
          return rect.top < window.innerHeight * 0.58 && rect.bottom > window.innerHeight * 0.42;
        });

        this.setActiveStep(initialIndex >= 0 ? initialIndex : 0);
      }
    }
  );
}
